import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FiPhone, FiMail, FiCheck } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import PresentationModal from '../../organisms/PresentationModal';
import Dialog from '../../organisms/Dialog';
import ATable from '../../organisms/ATable';
import CallRegistryModalForm from '../communication/contact/CallRegistryModalForm';
import appConfig from '../../../config/app-config';
import { formatCurrency, formatDate, formatCpfCnpj } from './collateralUtils';

const API = appConfig.apis.communicationApiEnv;

interface Props {
    show: boolean;
    onClose: () => void;
    contactId?: string;
    debtorName: string;
    debtorDocument: string;
    receivables: any[];
    operationId: string;
}

interface ContactData {
    id: string;
    name: string;
    taxId: string;
    phones: Array<{ id: string; number: string; whatsapp: boolean }>;
    emails: Array<{ id: string; address: string }>;
}

const DebtorContactModal = ({ show, onClose, contactId, debtorName, debtorDocument, receivables, operationId }: Props) => {
    const [contact, setContact] = useState<ContactData | null>(null);
    const [loading, setLoading] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [showCallForm, setShowCallForm] = useState(false);

    const totals = useMemo(() => ({
        count: receivables.length,
        amount: receivables.reduce((s: number, r: any) => s + (r.amount || 0), 0),
    }), [receivables]);

    useEffect(() => {
        if (!show) return;
        setLoading(true);
        setNotFound(false);
        setContact(null);

        const doFetch = async () => {
            try {
                const { Auth } = await import('aws-amplify');
                const session = await Auth.currentSession();
                const token = session.getIdToken().getJwtToken();
                const headers: Record<string, string> = {
                    'x-api-key': API.key || '',
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                };

                let matchId = contactId;

                if (!matchId) {
                    // Fallback: search by tax_id
                    const digits = debtorDocument.replace(/\D/g, '');
                    const query = digits.length >= 3 ? digits : debtorName;
                    if (query.length < 3) { setNotFound(true); setLoading(false); return; }

                    const searchRes = await fetch(`${API.address}/contacts/search?q=${encodeURIComponent(query)}`, { headers });
                    const searchData = await searchRes.json();
                    const match = digits.length >= 3
                        ? searchData.items?.find((c: any) => c.taxId?.replace(/\D/g, '') === digits)
                        : searchData.items?.[0];
                    if (!match) { setNotFound(true); setLoading(false); return; }
                    matchId = match.id;
                }

                const detailRes = await fetch(`${API.address}/contacts/${matchId}/page-view`, { headers });
                const detail = await detailRes.json();

                const phones = (detail.contactPoints || [])
                    .filter((cp: any) => (cp.type === 'PHONE' || cp.type === 'WHATSAPP') && cp.isActive)
                    .map((cp: any) => ({ id: cp.id, number: cp.valueFormatted, whatsapp: cp.isWhatsapp || cp.type === 'WHATSAPP' }));
                const emails = (detail.contactPoints || [])
                    .filter((cp: any) => cp.type === 'EMAIL' && cp.isActive)
                    .map((cp: any) => ({ id: cp.id, address: cp.valueFormatted }));

                setContact({ id: detail.id, name: detail.fullName, taxId: detail.taxIdFormatted || detail.taxId, phones, emails });
            } catch (err) {
                console.error('Error fetching contact:', err);
                setNotFound(true);
            } finally {
                setLoading(false);
            }
        };
        doFetch();
    }, [show, debtorDocument, debtorName]);

    const handleAction = (action: 'call' | 'email' | 'validate') => {
        const now = new Date().toLocaleString('pt-BR');
        const labels = { call: 'Ligação registrada', email: 'E-mail enviado', validate: 'Validação registrada' };
        toast.success(`${labels[action]} em ${now}`);
        // TODO: integrar com API quando disponível
    };

    const columns = [
        { id: 'titleNumber', title: 'NF', field: 'titleNumber', canSort: false, width: '70px' },
        { id: 'issueDate', title: 'Emissão', field: 'issueDate', canSort: false, width: '90px' },
        { id: 'dueDate', title: 'Vencimento', field: 'dueDate', canSort: false, width: '90px' },
        { id: 'amount', title: 'Valor NF', field: 'amount', canSort: false, cssClass: 'text-right', width: '110px' },
        { id: 'docStatus', title: 'Status', field: 'docStatus', canSort: false, width: '90px' },
    ];

    const deals = useMemo(() => receivables.map(r => ({
        id: `${r.nfeKey}_${r.receivableNumber}`,
        operation: operationId,
        number: `${r.nfeKey}_${r.receivableNumber}`,
        assignor: debtorName,
        value: String(r.amount),
        status: r.status,
    })), [receivables, operationId, debtorName]);

    if (!show) return null;

    return (
        <div className="fixed top-0 right-0 h-full w-[800px] bg-white dark:bg-slate-800 shadow-xl z-40 flex flex-col border-l border-gray-200 dark:border-slate-700 animate-slide-in-right">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{debtorName}</h3>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{debtorDocument}</span>
                </div>
                <div className="flex items-center gap-2">
                    {contact && (
                        <>
                            <Link to={`/communication/contact/${contact.id}/page-view/tab/deals`}
                                className="px-3 py-1.5 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 hover:underline">
                                Ver contato
                            </Link>
                            <button onClick={() => setShowCallForm(true)}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                                <FiPhone size={13} /> Registrar Ligação
                            </button>
                        </>
                    )}
                    <button onClick={onClose}
                        className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                        title="Fechar">
                        <span className="text-xl leading-none">&times;</span>
                    </button>
                </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-auto p-4 space-y-3">
                {/* Contact data */}
                <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400">{debtorDocument || 'Sem documento'}</span>
                        {loading && <span className="text-xs text-gray-400">Buscando contato...</span>}
                    </div>
                    {notFound && <p className="text-xs text-gray-500">Contato não encontrado no sistema.</p>}
                    {contact && (
                        <div className="flex flex-wrap gap-3">
                            {contact.phones.map(p => (
                                <div key={p.id} className="flex items-center gap-1.5 text-sm dark:text-white">
                                    {p.whatsapp ? <FaWhatsapp className="text-green-500" size={13} /> : <FiPhone className="text-gray-500" size={13} />}
                                    <span>{p.number}</span>
                                </div>
                            ))}
                            {contact.emails.map(e => (
                                <div key={e.id} className="flex items-center gap-1.5 text-sm dark:text-white">
                                    <FiMail className="text-gray-500" size={13} />
                                    <span>{e.address}</span>
                                </div>
                            ))}
                            {contact.phones.length === 0 && contact.emails.length === 0 && (
                                <span className="text-xs text-gray-500">Nenhum canal ativo</span>
                            )}
                        </div>
                    )}
                </div>

                {/* Titles table */}
                <div className="overflow-auto max-h-[300px]">
                    <ATable headers={columns}>
                    {receivables.map((r: any, index: number) => (
                        <tr key={r.id} className={index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'}>
                            <td className="py-1.5 px-2 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white">{r.titleNumber}</td>
                            <td className="py-1.5 px-2 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white">{formatDate(r.issueDate)}</td>
                            <td className="py-1.5 px-2 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white">{formatDate(r.dueDate)}</td>
                            <td className="py-1.5 px-2 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white text-right">{formatCurrency(r.amount)}</td>
                            <td className="py-1.5 px-2 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white">{r.docStatus || '—'}</td>
                        </tr>
                    ))}
                    </ATable>
                </div>
            </div>

            {showCallForm && contact && (
                <CallRegistryModalForm
                    contactId={contact.id}
                    availableDeals={deals as any}
                    preSelectedDeals={deals as any}
                    onClose={() => setShowCallForm(false)}
                    onSuccess={() => { setShowCallForm(false); toast.success('Ligação registrada!'); }}
                />
            )}
        </div>
    );
};

export default DebtorContactModal;
